import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import Navbar from "../components/Navbar";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, token }),
      });
      if (!res.ok) throw new Error("Failed to send message");
      setSent(true);
      resetForm();
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          background: "linear-gradient(120deg, #f1f5f9 60%, #f9fafc 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 8, md: 0 },
        }}
      >
        <Container maxWidth="md" sx={{ p: 0 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: "32px",
              backgroundColor: "#ffffff",
              boxShadow: "0 12px 40px 0 rgba(30,64,175,0.09)",
              mx: "auto",
              maxWidth: 900,
              minWidth: { md: 760 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "stretch",
              justifyContent: "center",
              gap: { xs: 2, md: 4 },
            }}
          >
            {/* Info section */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                pr: { md: 5 },
                mb: { xs: 3, md: 0 },
                borderRight: { md: "1px solid #f1f5f9" },
              }}
            >
              <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                Get in touch with us
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                Questions, feedback, or just want to say hello? We’re real
                humans and love to help. <br />
                Expect a reply within a few hours.
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, fontStyle: "italic" }}
              >
                🕐 Mon–Fri, 9:00am–5:00pm (GMT)
              </Typography>
              <Typography variant="body2" sx={{ color: "#1e3a8a" }}>
                📧 <strong>support@sortlybooks.com</strong>
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: 2 }}
              >
                Need help fast? Use the form — it pings us instantly!
              </Typography>
            </Box>

            {/* Contact form section */}
            <Box sx={{ flex: 2, minWidth: { xs: "100%", md: 320 } }}>
              <Formik
                initialValues={{
                  name: "",
                  email: "",
                  subject: "General",
                  message: "",
                }}
                validationSchema={Yup.object({
                  name: Yup.string().required("Name is required"),
                  email: Yup.string()
                    .email("Invalid email")
                    .required("Email is required"),
                  subject: Yup.string().required(),
                  message: Yup.string()
                    .min(10, "Please enter at least 10 characters")
                    .required("Message is required"),
                })}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, errors, touched, handleBlur }) => (
                  <Form>
                    <TextField
                      fullWidth
                      name="name"
                      label="Your Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      margin="normal"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      sx={{
                        backgroundColor: "#f1f5f9",
                        borderRadius: 2,
                        mb: 2,
                      }}
                    />
                    <TextField
                      fullWidth
                      name="email"
                      label="Email Address"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      margin="normal"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      sx={{
                        backgroundColor: "#f1f5f9",
                        borderRadius: 2,
                        mb: 2,
                      }}
                    />
                    <TextField
                      select
                      fullWidth
                      name="subject"
                      label="Subject"
                      value={values.subject}
                      onChange={handleChange}
                      margin="normal"
                      sx={{
                        backgroundColor: "#f1f5f9",
                        borderRadius: 2,
                        mb: 2,
                      }}
                    >
                      <MenuItem value="General">General</MenuItem>
                      <MenuItem value="Billing">Billing</MenuItem>
                      <MenuItem value="Technical">Technical Issue</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      name="message"
                      label="Message"
                      multiline
                      rows={4}
                      value={values.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      margin="normal"
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      sx={{
                        backgroundColor: "#f1f5f9",
                        borderRadius: 2,
                        mb: 2,
                      }}
                    />
                    <Box sx={{ my: 2 }}>
                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(val) => setToken(val)}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disabled={loading || !token}
                      startIcon={loading && <CircularProgress size={16} />}
                      sx={{
                        mt: 1,
                        borderRadius: "10px",
                        fontWeight: 700,
                        py: 1.3,
                        backgroundColor: "#2563eb",
                        fontSize: "1.05rem",
                        "&:hover": { backgroundColor: "#1e40af" },
                        textTransform: "none",
                        letterSpacing: 0.2,
                        boxShadow: "0 1px 10px 0 rgba(37,99,235,0.08)",
                      }}
                    >
                      {loading ? "Sending..." : "Send Message"}
                    </Button>
                  </Form>
                )}
              </Formik>
            </Box>
          </Paper>
        </Container>

        <Snackbar
          open={sent}
          autoHideDuration={5000}
          onClose={() => setSent(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSent(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            Your message has been sent — we’ll be in touch soon!
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError("")}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
